/**
 * Built-in Realtime Handlers - 实时事件处理层
 *
 * 职责：
 * 1. 处理各种系统实时事件（用户登出、封禁、系统通知等）
 * 2. 专注于事件响应逻辑，不关注具体业务实现细节
 * 3. 通过 Realtime Bridge 调用业务能力
 * 4. 提供多语言支持和错误处理
 *
 * 依赖关系：
 * - 依赖 Realtime Bridge 的业务接口
 * - 间接依赖 Auth Provider 的认证能力
 */

import { toast } from "@/components/pop-ui/pop-toast"
import { getRealtimeBridgeMethods } from "@/providers/realtime-bridge-provider"
import type { RealtimeManager } from "./realtime-manager"
import {
	RealtimeEventType,
	type UserBannedPayload,
	type UserDeactivatedPayload,
	type UserLogoutPayload,
} from "./types"

/**
 * 获取多语言翻译函数
 */
async function getTranslation() {
	try {
		const { getT } = await import("@/providers/translations-provider")
		return getT()
	} catch (error) {
		console.warn("Failed to load translation function:", error)
		// 返回默认的翻译函数
		return (key: string, fallback?: string) => fallback || key
	}
}

/**
 * Register all built-in event handlers
 */
export function registerBuiltInHandlers(manager: RealtimeManager): void {
	// User logout handler
	manager.registerHandler<UserLogoutPayload>({
		id: "built-in-user-logout",
		eventType: RealtimeEventType.USER_LOGOUT,
		priority: 1, // High priority
		handler: async (payload) => {
			try {
				const t = await getTranslation()
				const bridgeMethods = getRealtimeBridgeMethods()

				if (bridgeMethods) {
					// 使用 bridge 方法处理退出登录
					await bridgeMethods.handleUserLogout({
						redirectUrl: payload.redirectUrl || "/user/login",
						clearQueryCache: true,
						showToast: true,
						toastMessage:
							payload.reason ||
							t(
								"realtime.user.logout.forced",
								"You have been forced to logout",
							),
					})
				} else {
					console.warn("Bridge methods not available, using fallback")
					// 备用处理：直接重新加载页面
					window.location.href = payload.redirectUrl || "/"
				}
			} catch (error) {
				console.error("Error handling user logout:", error)
				// 备用处理：直接重新加载页面
				window.location.href = payload.redirectUrl || "/"
			}
		},
	})

	// User banned handler
	manager.registerHandler<UserBannedPayload>({
		id: "built-in-user-banned",
		eventType: RealtimeEventType.USER_BANNED,
		priority: 1, // High priority
		handler: async (payload) => {
			try {
				const t = await getTranslation()

				// Show appropriate message based on ban type
				const message =
					payload.banType === "permanent"
						? t(
								"realtime.user.banned.permanent",
								"Your account has been permanently banned.",
								{
									reason: payload.reason,
								},
							)
						: t(
								"realtime.user.banned.temporary",
								"Your account has been temporarily banned.",
								{
									reason: payload.reason,
									expiresAt: payload.expiresAt
										? t("realtime.user.banned.until", "至 {{date}}", {
												date: new Date(payload.expiresAt).toLocaleString(),
											})
										: "",
								},
							)

				toast.error(message, {
					duration: 8000,
				})

				// 使用 bridge 方法处理退出登录
				setTimeout(async () => {
					const bridgeMethods = getRealtimeBridgeMethods()
					if (bridgeMethods) {
						await bridgeMethods.handleUserLogout({
							redirectUrl: "/",
							clearQueryCache: true,
							showToast: false, // 已经显示过toast了
						})
					} else {
						console.warn("Bridge methods not available, using fallback")
						window.location.href = "/"
					}
				}, 2000)
			} catch (error) {
				console.error("Error handling user banned:", error)
				// 备用处理：直接重新加载页面
				setTimeout(() => {
					window.location.href = "/"
				}, 2000)
			}
		},
	})

	// User deactivated handler
	manager.registerHandler<UserDeactivatedPayload>({
		id: "built-in-user-deactivated",
		eventType: RealtimeEventType.USER_DEACTIVATED,
		priority: 1, // High priority
		handler: async (payload) => {
			try {
				const t = await getTranslation()

				// Show notification
				toast.error(
					t(
						"realtime.user.deactivated.message",
						"Your account has been deactivated.",
						{
							reason: payload.reason,
						},
					),
					{
						duration: 6000,
					},
				)

				// 使用 bridge 方法处理退出登录
				setTimeout(async () => {
					const bridgeMethods = getRealtimeBridgeMethods()
					if (bridgeMethods) {
						await bridgeMethods.handleUserLogout({
							redirectUrl: payload.redirectUrl || "/",
							clearQueryCache: true,
							showToast: false, // 已经显示过toast了
						})
					} else {
						console.warn("Bridge methods not available, using fallback")
						window.location.href = payload.redirectUrl || "/"
					}
				}, 1500)
			} catch (error) {
				console.error("Error handling user deactivated:", error)
				// 备用处理：直接重新加载页面
				setTimeout(() => {
					window.location.href = payload.redirectUrl || "/"
				}, 1500)
			}
		},
	})
}
